import { useState } from "react";
import {
  Sparkles,
  Droplets,
  Heart,
  ShoppingBag,
  Loader2,
  Quote,
  Leaf,
  Flame,
  Wind,
  HandHeart,
  Star,
} from "lucide-react";
import { useCheckout } from "@/lib/useCheckout";

const OIL_SIZES = [
  { id: "oil-hair-2oz", label: "2 fl oz", subLabel: "60 ml", price: 18, image: "/assets/oil_2oz.png", note: "Try-me size" },
  { id: "oil-hair-4oz", label: "4 fl oz", subLabel: "120 ml", price: 28, image: "/assets/oil_4oz.png", note: "Founder's pick", featured: true },
];

const HOT_OIL = { id: "oil-hot-treatment", label: "Hot Oil Treatment", price: 22 };

const usageSteps = [
  {
    icon: Wind,
    title: "Part Your Edges",
    body: "Part your edges all the way around your hairline so every strand gets kissed by the oil.",
  },
  {
    icon: Leaf,
    title: "Divide Into Four",
    body: "Divide the remaining hair into four clean sections — this makes even application effortless.",
  },
  {
    icon: Droplets,
    title: "Apply & Massage",
    body: "Apply the Hair Growth Oil directly to each section of your scalp and along your edges. Gently massage into the scalp for 2–3 minutes to encourage even distribution.",
  },
  {
    icon: Heart,
    title: "Repeat Twice Daily",
    body: "Repeat this routine twice daily — once in the morning and once at night. Consistency is where the growth lives.",
  },
];

const restoreWords = ["Restore.", "Repair.", "Replenish.", "Radiate."];

const testimonies = [
  {
    name: "Aisha M.",
    location: "Milwaukee, WI",
    quote:
      "Six weeks in and my edges are back. I&apos;ve tried everything — this is the only thing that made a visible difference. My scalp finally feels healthy.",
    stars: 5,
  },
  {
    name: "Danielle R.",
    location: "Chicago, IL",
    quote:
      "The scent is so gentle and it never feels greasy. My hair holds twist-outs 3 days longer now. I&apos;m on my third bottle.",
    stars: 5,
  },
  {
    name: "Keisha W.",
    location: "Atlanta, GA",
    quote:
      "I gifted this to my mama for her thinning crown. She called me crying after two months. That was the moment I knew this brand was different.",
    stars: 5,
  },
  {
    name: "Trina B.",
    location: "Detroit, MI",
    quote:
      "The hot oil treatment is my Sunday reset. I light a 414 candle, warm the oil, and take that 20 minutes just for me. That&apos;s church.",
    stars: 5,
  },
];

export default function Oils() {
  const [size, setSize] = useState(OIL_SIZES[1]);
  const { buy, loadingId } = useCheckout();

  return (
    <div data-testid="oils-page" className="bg-brand-cream">
      {/* ============== HERO — PRODUCT LEFT · FOUNDER STORY RIGHT ============== */}
      <section className="relative overflow-hidden pt-16 lg:pt-24 pb-24 lg:pb-32">
        <img
          src="/assets/flower_rose_pale.png"
          alt=""
          aria-hidden="true"
          className="absolute -top-12 -left-16 w-72 opacity-70 pointer-events-none hidden lg:block"
        />
        <img
          src="/assets/flower_bluebell.png"
          alt=""
          aria-hidden="true"
          className="absolute bottom-8 -right-14 w-64 opacity-70 pointer-events-none hidden lg:block"
        />

        <div className="relative max-w-7xl mx-auto px-6 lg:px-12">
          <div className="text-center max-w-3xl mx-auto mb-14 lg:mb-20">
            <span
              className="inline-block bg-brand-moss text-brand-gold px-6 py-1.5 text-xs uppercase tracking-[0.35em] -rotate-1"
              data-testid="oils-eyebrow"
            >
              414, LLC Hair Growth Oil
            </span>
            <h1 className="mt-6 font-serif text-4xl sm:text-5xl lg:text-6xl text-brand-moss leading-tight">
              Rooted in Faith.
              <br />
              <span className="italic text-[#B7574E]">Growing with Purpose.</span>
            </h1>
            <p className="mt-6 text-lg text-brand-ink/75 leading-relaxed">
              Nourish. Strengthen. Thrive. &mdash; made with natural oils and botanical extracts,
              hand-blended in small batches with prayer over every pour.
            </p>
          </div>

          <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-start">
            {/* ---------- LEFT: BIG PRODUCT HERO ---------- */}
            <div
              data-testid="hero-product-card"
              className="relative bg-gradient-to-br from-[#F0E9D6] to-[#F8ECD9] rounded-[2.5rem] overflow-hidden border border-brand-moss/10 shadow-lg"
            >
              <div className="relative aspect-square w-full overflow-hidden bg-brand-parchment">
                <img
                  src={size.image}
                  alt={`414 LLC Hair Growth Oil — ${size.label}`}
                  className="w-full h-full object-cover transition-opacity duration-500"
                  key={size.id}
                />
                <span className="absolute top-5 left-5 inline-flex items-center gap-2 bg-brand-moss text-brand-gold px-4 py-1.5 text-[10px] uppercase tracking-[0.3em] rounded-full shadow">
                  <Sparkles className="w-3.5 h-3.5" />
                  Signature Product
                </span>
              </div>

              <div className="p-8 lg:p-10">
                <h2 className="font-serif text-3xl lg:text-4xl text-brand-moss leading-tight">
                  Hair Growth Oil
                </h2>
                <p className="mt-2 text-sm uppercase tracking-[0.3em] text-brand-moss/60">
                  Nourish &middot; Strengthen &middot; Thrive
                </p>

                <div className="mt-6 flex items-baseline gap-3">
                  <span className="font-serif text-5xl text-brand-moss">${size.price}</span>
                  <span className="text-xs uppercase tracking-[0.25em] text-brand-ink/60">
                    / {size.label} &middot; {size.subLabel}
                  </span>
                </div>

                {/* Size selector */}
                <div className="mt-6">
                  <p className="text-xs uppercase tracking-[0.3em] text-brand-moss/60 mb-3">
                    Choose your size
                  </p>
                  <div className="flex flex-wrap gap-3">
                    {OIL_SIZES.map((s) => {
                      const active = s.id === size.id;
                      return (
                        <button
                          key={s.id}
                          data-testid={`size-${s.id}-btn`}
                          onClick={() => setSize(s)}
                          className={`relative flex-1 min-w-[140px] px-4 py-3 rounded-2xl border-2 transition-all duration-300 text-left ${
                            active
                              ? "border-brand-moss bg-brand-moss text-brand-gold shadow-md"
                              : "border-brand-moss/20 bg-white/60 text-brand-moss hover:border-brand-moss/50"
                          }`}
                        >
                          <div className="font-serif text-xl leading-none">{s.label}</div>
                          <div
                            className={`mt-1 text-[10px] uppercase tracking-[0.25em] ${
                              active ? "text-brand-gold/90" : "text-brand-moss/60"
                            }`}
                          >
                            {s.subLabel} &middot; ${s.price}
                          </div>
                          {s.featured && (
                            <span className="absolute -top-2 -right-2 bg-[#B7574E] text-brand-cream text-[9px] uppercase tracking-widest px-2 py-0.5 rounded-full">
                              Best
                            </span>
                          )}
                        </button>
                      );
                    })}
                  </div>
                </div>

                <button
                  type="button"
                  data-testid={`buy-${size.id}-btn`}
                  onClick={() => buy(size.id, `Hair Growth Oil ${size.label}`)}
                  disabled={loadingId === size.id}
                  className="mt-6 w-full inline-flex items-center justify-center gap-2 bg-brand-moss text-brand-gold px-8 py-4 text-xs uppercase tracking-[0.35em] rounded-full hover:bg-brand-moss/90 disabled:opacity-60 disabled:cursor-not-allowed transition-all duration-300"
                >
                  {loadingId === size.id ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin" />
                      Opening checkout&hellip;
                    </>
                  ) : (
                    <>
                      <ShoppingBag className="w-4 h-4" strokeWidth={1.8} />
                      Add to Cart &middot; ${size.price}
                    </>
                  )}
                </button>

                {/* Hot oil upsell */}
                <div className="mt-5 rounded-2xl border border-[#B7574E]/20 bg-[#F3E3DF]/60 p-4 flex items-center justify-between gap-4">
                  <div>
                    <div className="flex items-center gap-2 text-brand-moss">
                      <Flame className="w-4 h-4" />
                      <p className="font-serif text-lg leading-tight">{HOT_OIL.label}</p>
                    </div>
                    <p className="mt-0.5 text-xs text-brand-ink/70">
                      Weekly deep-condition ritual &middot; ${HOT_OIL.price}
                    </p>
                  </div>
                  <button
                    type="button"
                    data-testid={`buy-${HOT_OIL.id}-btn`}
                    onClick={() => buy(HOT_OIL.id, HOT_OIL.label)}
                    disabled={loadingId === HOT_OIL.id}
                    className="shrink-0 inline-flex items-center gap-1.5 bg-[#B7574E] text-brand-cream px-4 py-2 text-[10px] uppercase tracking-[0.3em] rounded-full hover:bg-[#a04a42] disabled:opacity-60 disabled:cursor-not-allowed transition-all"
                  >
                    {loadingId === HOT_OIL.id ? (
                      <Loader2 className="w-3 h-3 animate-spin" />
                    ) : (
                      <>Add</>
                    )}
                  </button>
                </div>
              </div>
            </div>

            {/* ---------- RIGHT: FOUNDER STORY ---------- */}
            <div data-testid="founder-testimony" className="lg:pt-4">
              <span className="inline-block text-xs uppercase tracking-[0.35em] text-[#B7574E]">
                <Quote className="inline w-4 h-4 mr-1 -mt-1" />
                A note from the founder
              </span>
              <h3 className="mt-4 font-serif text-3xl lg:text-4xl text-brand-moss leading-snug">
                Born from curiosity, personal experience,
                <br />
                <span className="italic text-[#B7574E]">perseverance, and prayer.</span>
              </h3>

              {/* Duo product photo */}
              <figure className="mt-8 relative rounded-3xl overflow-hidden shadow-md border border-brand-moss/10 bg-brand-parchment">
                <img
                  src="/assets/oil_duo.png"
                  alt="414 LLC Hair Growth Oil — 4 fl oz and 2 fl oz side by side"
                  className="w-full h-auto object-cover"
                />
                <figcaption className="absolute bottom-0 inset-x-0 bg-gradient-to-t from-brand-moss/85 via-brand-moss/40 to-transparent p-4">
                  <p className="text-brand-gold text-xs uppercase tracking-[0.3em]">
                    Available in 4 fl oz &amp; 2 fl oz
                  </p>
                  <p className="text-brand-cream text-sm font-serif italic">
                    Made with natural oils &amp; botanical extracts.
                  </p>
                </figcaption>
              </figure>

              {/* Founder body copy — user's exact words */}
              <div className="mt-8 space-y-4 text-brand-ink/85 leading-relaxed">
                <p>
                  414, LLC Hair Growth Oil was born from curiosity, personal experience,
                  perseverance, and prayer. After carefully researching, experimenting with natural
                  oils, and developing a consistent hair care routine, I began using it on my own
                  scalp, my daughter&rsquo;s, as well as on family members and trusted friends.
                </p>
                <p>
                  Over time, many shared positive experiences, noting healthier-looking hair,
                  improved moisture, reduced breakage, and fuller, stronger hair.
                </p>
                <p className="font-serif text-xl italic text-brand-moss">
                  Consistency is key. &mdash; Chatoria
                </p>
              </div>

              <div className="mt-8 flex flex-wrap gap-3">
                <span className="inline-flex items-center gap-2 bg-white/70 border border-brand-moss/15 px-4 py-2 rounded-full text-xs uppercase tracking-[0.25em] text-brand-moss">
                  <Leaf className="w-3.5 h-3.5" />
                  Natural Oils
                </span>
                <span className="inline-flex items-center gap-2 bg-white/70 border border-brand-moss/15 px-4 py-2 rounded-full text-xs uppercase tracking-[0.25em] text-brand-moss">
                  <Droplets className="w-3.5 h-3.5" />
                  Botanical Extracts
                </span>
                <span className="inline-flex items-center gap-2 bg-white/70 border border-brand-moss/15 px-4 py-2 rounded-full text-xs uppercase tracking-[0.25em] text-brand-moss">
                  <Heart className="w-3.5 h-3.5" />
                  Made in USA
                </span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ============== 4 EASY-BREEZY STEPS ============== */}
      <section
        data-testid="usage-guide-section"
        className="relative bg-brand-moss text-brand-cream py-24 lg:py-32 overflow-hidden"
      >
        <img
          src="/assets/flower_leaf.png"
          alt=""
          aria-hidden="true"
          className="absolute top-8 right-8 w-40 opacity-30 pointer-events-none hidden md:block"
        />
        <div className="relative max-w-7xl mx-auto px-6 lg:px-12">
          <div className="text-center max-w-2xl mx-auto">
            <span className="inline-block bg-brand-gold text-brand-moss px-6 py-1.5 text-xs uppercase tracking-[0.35em] rotate-1">
              4 Easy-Breezy Steps
            </span>
            <h2 className="mt-6 font-serif text-4xl lg:text-5xl leading-tight">
              Make it part of your
              <br />
              <span className="italic text-brand-gold">daily hair care routine.</span>
            </h2>
            <p className="mt-5 text-brand-cream/80 text-lg">
              Follow these 4 Easy-Breezy Steps &mdash; consistency is the difference between a
              bottle on the shelf and a testimony you&apos;ll be telling next season.
            </p>
          </div>

          <ol className="mt-16 grid md:grid-cols-2 gap-6 lg:gap-8">
            {usageSteps.map((step, idx) => {
              const Icon = step.icon;
              return (
                <li
                  key={step.title}
                  data-testid={`usage-step-${idx + 1}`}
                  className="relative bg-brand-cream/5 backdrop-blur-sm border border-brand-gold/20 rounded-3xl p-8 hover:bg-brand-cream/10 transition-all duration-500 hover:-translate-y-1"
                >
                  <div className="flex items-center justify-between">
                    <span className="font-serif text-6xl text-brand-gold/40 leading-none">
                      {String(idx + 1).padStart(2, "0")}
                    </span>
                    <span className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-brand-gold text-brand-moss">
                      <Icon className="w-5 h-5" strokeWidth={1.7} />
                    </span>
                  </div>
                  <h3 className="mt-5 font-serif text-2xl text-brand-cream leading-tight">
                    Step {idx + 1}: {step.title}
                  </h3>
                  <p
                    className="mt-3 text-sm text-brand-cream/75 leading-relaxed"
                    dangerouslySetInnerHTML={{ __html: step.body }}
                  />
                </li>
              );
            })}
          </ol>

          <p className="mt-12 text-center text-sm text-brand-cream/70 max-w-2xl mx-auto italic">
            With consistency, many customers report healthier-looking, softer, stronger, and more
            manageable hair over time.
          </p>
        </div>
      </section>

      {/* ============== RESTORE. REPAIR. REPLENISH. RADIATE. ============== */}
      <section
        data-testid="restore-band"
        className="relative bg-brand-parchment py-20 overflow-hidden border-y border-brand-moss/10"
      >
        <div className="relative max-w-6xl mx-auto px-6 lg:px-12 text-center">
          <div className="flex flex-wrap items-center justify-center gap-x-8 gap-y-4">
            {restoreWords.map((word, i) => (
              <span
                key={word}
                className="inline-flex items-center gap-6"
              >
                <span className="font-serif text-3xl md:text-4xl lg:text-5xl text-brand-moss italic">
                  {word}
                </span>
                {i < restoreWords.length - 1 && (
                  <HandHeart className="w-5 h-5 text-brand-gold hidden md:inline" />
                )}
              </span>
            ))}
          </div>
          <p className="mt-10 text-brand-ink/75 max-w-xl mx-auto leading-relaxed">
            Thank you for trusting <span className="font-serif italic text-brand-moss">414, LLC</span>{" "}
            with your healthy hair journey.
          </p>
        </div>
      </section>

      {/* ============== COMMUNITY TESTIMONIES ============== */}
      <section
        data-testid="community-testimonies"
        className="relative bg-brand-cream py-24 lg:py-32 overflow-hidden"
      >
        <img
          src="/assets/flower_bouquet.png"
          alt=""
          aria-hidden="true"
          className="absolute -top-8 left-1/2 -translate-x-1/2 w-56 opacity-70 pointer-events-none hidden md:block"
        />
        <div className="relative max-w-7xl mx-auto px-6 lg:px-12">
          <div className="text-center max-w-2xl mx-auto">
            <span className="inline-block bg-brand-moss text-brand-gold px-6 py-1.5 text-xs uppercase tracking-[0.35em] -rotate-1">
              Community Testimonies
            </span>
            <h2 className="mt-6 font-serif text-4xl lg:text-5xl text-brand-moss leading-tight">
              What our sisters are saying.
            </h2>
            <p className="mt-5 text-lg text-brand-ink/75">
              Real customers. Real crowns. Real testimonies.
            </p>
          </div>

          <div className="mt-16 grid md:grid-cols-2 gap-6">
            {testimonies.map((t, i) => (
              <blockquote
                key={t.name}
                data-testid={`testimony-${i + 1}`}
                className="relative bg-white/70 border border-brand-moss/10 rounded-3xl p-8 shadow-sm hover:shadow-md transition-all duration-500"
              >
                <Quote className="absolute top-6 right-6 w-8 h-8 text-brand-moss/15" />
                <div className="flex items-center gap-1 mb-4 text-[#B7574E]">
                  {Array.from({ length: t.stars }).map((_, s) => (
                    <Star key={s} className="w-4 h-4 fill-current" />
                  ))}
                </div>
                <p
                  className="text-brand-ink/85 leading-relaxed italic"
                  dangerouslySetInnerHTML={{ __html: `&ldquo;${t.quote}&rdquo;` }}
                />
                <footer className="mt-6 pt-4 border-t border-brand-moss/10 flex items-center justify-between">
                  <div>
                    <cite className="not-italic font-serif text-lg text-brand-moss">{t.name}</cite>
                    <p className="text-xs uppercase tracking-[0.25em] text-brand-moss/60 mt-0.5">
                      {t.location}
                    </p>
                  </div>
                  <span className="text-[10px] uppercase tracking-[0.3em] text-brand-moss/50">
                    Verified Buyer
                  </span>
                </footer>
              </blockquote>
            ))}
          </div>

          <div className="mt-16 text-center">
            <button
              type="button"
              data-testid="cta-order-oil-btn"
              onClick={() => buy(size.id, `Hair Growth Oil ${size.label}`)}
              disabled={loadingId === size.id}
              className="inline-flex items-center gap-3 bg-brand-moss text-brand-gold px-10 py-4 text-xs uppercase tracking-[0.35em] rounded-full hover:bg-brand-moss/90 disabled:opacity-60 disabled:cursor-not-allowed transition-all duration-300 shadow-lg"
            >
              {loadingId === size.id ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Opening checkout&hellip;
                </>
              ) : (
                <>
                  <ShoppingBag className="w-4 h-4" strokeWidth={1.8} />
                  Start your own journey &middot; ${size.price}
                </>
              )}
            </button>
            <p className="mt-4 text-sm text-brand-ink/60">
              Free shipping on orders over $75 &middot; Ships in 2 business days
            </p>
          </div>
        </div>
      </section>
    </div>
  );
}
